import React, { Component } from 'react';
import moment from 'moment';
import SectionHeader from '../../components/views/SectionHeader';
import SectionFooter from '../../components/views/SectionFooter';
import InputField from '../../components/inputs/inputField';
import SelectField from '../../components/inputs/selectField';
import TextAreaField from '../../components/inputs/textAreaField';
import API from '../../utils/API';

const statusOptions = [
	{ value: 'Draft', label: 'Draft', color: '#6c757d' },
	{ value: 'Sent', label: 'Sent', color: '#007bff' },
	{ value: 'Received', label: 'Received', color: '#17a2b8' },
	{ value: 'Review', label: 'Review', color: '#ffc107' },
	{ value: 'Shortlist', label: 'Shortlist', color: '#28a745' },
	{ value: 'Turndown', label: 'Turndown', color: '#dc3545' },
	{ value: 'Cancel', label: 'Cancel', color: '#6c757d' },
	{ value: 'Contracted', label: 'Contracted', color: '#28a745' }
];

class RFPStatus extends Component {
	constructor(props) {
		super(props);
		const { venue } = props;
		
		this.state = {
			editedVenue: {
				rfpStatus: venue.rfpStatus || {
					status: 'Draft',
					sentDate: null,
					responseDate: null,
					proposalUrl: '',
					proposalNotes: '',
					contractedDate: null,
					contractValue: 0,
					statusHistory: []
				}
			},
			editing: false,
			saving: false
		};
	}

	updateRFPStatus = (field, value) => {
		const { editedVenue } = this.state;
		const previousStatus = editedVenue.rfpStatus.status;
		
		editedVenue.rfpStatus[field] = value;
		
		// If status changed, add to history
		if (field === 'status' && value !== previousStatus) {
			editedVenue.rfpStatus.statusHistory.push({
				status: value,
				date: new Date(),
				notes: `Status changed from ${previousStatus} to ${value}`
			});
		}
		
		this.setState({ editedVenue, editing: true });
	};

	saveDetails = async () => {
		this.setState({ saving: true });
		
		try {
			await API().patch(
				`Organizations/${this.props.orgId}/sourced-venues/${this.props.venueId}`,
				{ rfpStatus: this.state.editedVenue.rfpStatus }
			);
			
			this.setState({ editing: false, saving: false });
			this.props.refreshSectionCounts && this.props.refreshSectionCounts();
		} catch (error) {
			console.error('Error saving RFP status:', error);
			this.setState({ saving: false });
		}
	};

	cancelEdit = () => {
		const { venue } = this.props;
		this.setState({
			editedVenue: {
				rfpStatus: venue.rfpStatus || {
					status: 'Draft',
					sentDate: null,
					responseDate: null,
					proposalUrl: '',
					proposalNotes: '',
					contractedDate: null,
					contractValue: 0,
					statusHistory: []
				}
			},
			editing: false
		});
	};

	render() {
		const { editedVenue, editing } = this.state;
		const { venue } = this.props;
		const { rfpStatus } = editedVenue;
		
		const sentDate = rfpStatus.sentDate ? moment(rfpStatus.sentDate).format('YYYY-MM-DD') : '';
		const responseDate = rfpStatus.responseDate ? moment(rfpStatus.responseDate).format('YYYY-MM-DD') : '';
		const contractedDate = rfpStatus.contractedDate ? moment(rfpStatus.contractedDate).format('YYYY-MM-DD') : '';
		
		const selectedStatus = statusOptions.find(opt => opt.value === rfpStatus.status);
		
		return (
			<div className="sectionContainer">
				<SectionHeader
					breadcrumbs={[{ name: venue.name }]}
					title="RFP Status & Tracking"
					description="Track the progress and status of your RFP"
				/>
				
				<div className="sectionBody">
					<div className="sectionBlock">
						<h2 className="blockHeader">Current Status</h2>
						
						<SelectField
							label="RFP Status"
							value={selectedStatus}
							options={statusOptions}
							onChange={(option) => this.updateRFPStatus('status', option.value)}
						/>
					</div>
					
					<div className="sectionBlock">
						<h2 className="blockHeader">Important Dates</h2>
						
						<div className="flex wrap" style={{ gap: '15px' }}>
							<div style={{ flex: '1 1 250px' }}>
								<InputField
									label="Date RFP Sent"
									type="date"
									value={sentDate}
									onChange={(e) => this.updateRFPStatus('sentDate', e.target.value)}
								/>
							</div>
							<div style={{ flex: '1 1 250px' }}>
								<InputField
									label="Date Response Received"
									type="date"
									value={responseDate}
									onChange={(e) => this.updateRFPStatus('responseDate', e.target.value)}
								/>
							</div>
						</div>
						
						{rfpStatus.status === 'Contracted' && (
							<div className="flex wrap" style={{ gap: '15px' }}>
								<div style={{ flex: '1 1 250px' }}>
									<InputField
										label="Contract Date"
										type="date"
										value={contractedDate}
										onChange={(e) => this.updateRFPStatus('contractedDate', e.target.value)}
									/>
								</div>
								<div style={{ flex: '1 1 250px' }}>
									<InputField
										label="Contract Value"
										type="number"
										value={rfpStatus.contractValue || ''}
										onChange={(e) => this.updateRFPStatus('contractValue', parseInt(e.target.value) || 0)}
										placeholder="$0"
									/>
								</div>
							</div>
						)}
					</div>
					
					<div className="sectionBlock">
						<h2 className="blockHeader">Proposal Information</h2>
						
						<InputField
							label="Proposal Document URL"
							value={rfpStatus.proposalUrl}
							onChange={(e) => this.updateRFPStatus('proposalUrl', e.target.value)}
							placeholder="https://..."
						/>
						
						{rfpStatus.proposalUrl && (
							<a href={rfpStatus.proposalUrl} target="_blank" rel="noopener noreferrer" className="flex aic mb-20">
								<i className="las la-external-link-alt mr-10" />
								View Proposal Document
							</a>
						)}
						
						<TextAreaField
							label="Proposal Notes"
							value={rfpStatus.proposalNotes}
							onChange={(e) => this.updateRFPStatus('proposalNotes', e.target.value)}
							placeholder="Add notes about the proposal, pricing, terms, or any important details..."
							rows={4}
						/>
					</div>
					
					{rfpStatus.statusHistory && rfpStatus.statusHistory.length > 0 && (
						<div className="sectionBlock">
							<h2 className="blockHeader">Status History</h2>
							<div className="timeline">
								{rfpStatus.statusHistory.slice().reverse().map((history, index) => {
									const historyStatus = statusOptions.find(opt => opt.value === history.status);
									return (
										<div key={index} className="timeline-item mb-15">
											<div className="flex aic">
												<div 
													className="timeline-dot mr-15"
													style={{ 
														width: '12px', 
														height: '12px', 
														borderRadius: '50%', 
														backgroundColor: historyStatus?.color || '#ccc' 
													}}
												/>
												<div>
													<h4 className="m-0">{history.status}</h4>
													<p className="grey small mb-0">
														{moment(history.date).format('MMM D, YYYY h:mm A')}
													</p>
													{history.notes && (
														<p className="mt-5 mb-0">{history.notes}</p>
													)}
												</div>
											</div>
										</div>
									);
								})}
							</div>
						</div>
					)}
				</div>
				
				<SectionFooter
					saveEdit={this.saveDetails}
					cancelEdit={this.cancelEdit}
					updatedAt={venue.updatedAt}
					editing={editing}
				/>
			</div>
		);
	}
}

export default RFPStatus;
